import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/api';

const SalesContext = createContext(null);

export const MOCK_SHOPS = [];
export const MOCK_MENU_ITEMS = [];

const TAX_RATE = 0.05;

export const SalesProvider = ({ children }) => {
  const [selectedShop, setSelectedShop] = useState(null);
  const [cart, setCart] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [recentShops, setRecentShops] = useState([]);
  const [menuByShopId, setMenuByShopId] = useState({});
  const [customShops, setCustomShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Load shops and menu from DB on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadError(null);
      try {
        const shopsRes = await apiService.sales.getShops();
        const shops = Array.isArray(shopsRes.data) ? shopsRes.data : [];
        if (cancelled) return;
        setCustomShops(shops);
        const menu = {};
        await Promise.all(
          shops.map(async (s) => {
            try {
              const menuRes = await apiService.sales.getMenuByShop(s.id);
              menu[s.id] = Array.isArray(menuRes.data) ? menuRes.data : [];
            } catch {
              menu[s.id] = [];
            }
          })
        );
        if (cancelled) return;
        setMenuByShopId(menu);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.response?.data?.error || err.message || 'Failed to load shops');
          setCustomShops([]);
          setMenuByShopId({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const allShops = React.useMemo(
    () => [...MOCK_SHOPS, ...customShops],
    [customShops]
  );

  const addShop = useCallback(async (shop) => {
    const res = await apiService.sales.createShop({
      name: shop.name || 'New Shop',
      subtitle: shop.subtitle || '',
      icon: shop.icon || '🏪',
    });
    const newShop = res.data;
    setCustomShops((prev) => [...prev, newShop]);
    setMenuByShopId((prev) => ({ ...prev, [newShop.id]: [] }));
    return newShop.id;
  }, []);

  const editShop = useCallback(async (shopId, updates) => {
    const res = await apiService.sales.updateShop(shopId, {
      name: updates.name,
      subtitle: updates.subtitle,
      icon: updates.icon,
    });
    const updated = res.data;
    setCustomShops((prev) =>
      prev.map((s) => (s.id === shopId ? { ...s, ...updated } : s))
    );
    setRecentShops((prev) =>
      prev.map((s) => (s.id === shopId ? { ...s, ...updated } : s))
    );
    setSelectedShop((current) =>
      current?.id === shopId ? { ...current, ...updated } : current
    );
  }, []);

  const deleteShop = useCallback(async (shopId) => {
    await apiService.sales.deleteShop(shopId);
    setCustomShops((prev) => prev.filter((s) => s.id !== shopId));
    setRecentShops((prev) => prev.filter((s) => s.id !== shopId));
    setSelectedShop((current) => (current?.id === shopId ? null : current));
    setMenuByShopId((prev) => {
      const next = { ...prev };
      delete next[shopId];
      return next;
    });
  }, []);

  const allMenuItems = React.useMemo(() => {
    if (!selectedShop) return [];
    const list = menuByShopId[selectedShop.id] || [];
    return [...MOCK_MENU_ITEMS, ...list];
  }, [selectedShop, menuByShopId]);

  const addMenuItem = useCallback(async (shopId, item) => {
    const res = await apiService.sales.addMenuItem(shopId, {
      name: item.name || 'New Item',
      description: item.description || '',
      price: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0,
      category: item.category || 'Best Sellers',
    });
    const newItem = res.data;
    setMenuByShopId((prev) => ({
      ...prev,
      [shopId]: [...(prev[shopId] || []), newItem],
    }));
    return newItem.id;
  }, []);

  const removeMenuItem = useCallback(async (shopId, itemId) => {
    await apiService.sales.removeMenuItem(shopId, itemId);
    setMenuByShopId((prev) => ({
      ...prev,
      [shopId]: (prev[shopId] || []).filter((m) => m.id !== itemId),
    }));
    setCart((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  const addToCart = useCallback((itemId, qty = 1) => {
    setCart((prev) => ({ ...prev, [itemId]: (prev[itemId] || 0) + qty }));
  }, []);

  const setCartQuantity = useCallback((itemId, qty) => {
    if (qty <= 0) {
      setCart((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    } else {
      setCart((prev) => ({ ...prev, [itemId]: qty }));
    }
  }, []);

  const removeFromCart = useCallback((itemId) => {
    setCart((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart({});
    setSpecialInstructions('');
  }, []);

  const selectShop = useCallback((shop) => {
    setSelectedShop(shop);
    setRecentShops((prev) => {
      const rest = prev.filter((s) => s.id !== shop?.id);
      return shop ? [shop, ...rest].slice(0, 5) : prev;
    });
  }, []);

  const cartItemCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartItemsWithDetails = Object.entries(cart)
    .map(([id, qty]) => {
      const item = allMenuItems.find((m) => m.id === id);
      return item ? { ...item, quantity: qty } : null;
    })
    .filter(Boolean);
  const subtotal = cartItemsWithDetails.reduce((a, i) => a + i.price * i.quantity, 0);
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  const value = {
    selectedShop,
    setSelectedShop: selectShop,
    cart,
    addToCart,
    setCartQuantity,
    removeFromCart,
    clearCart,
    specialInstructions,
    setSpecialInstructions,
    recentShops,
    allShops,
    addShop,
    editShop,
    deleteShop,
    menuByShopId,
    allMenuItems,
    addMenuItem,
    removeMenuItem,
    cartItemCount,
    cartItemsWithDetails,
    subtotal,
    tax,
    total,
    TAX_RATE,
    loading,
    loadError,
  };

  return (
    <SalesContext.Provider value={value}>
      {children}
    </SalesContext.Provider>
  );
};

export const useSales = () => {
  const ctx = useContext(SalesContext);
  if (!ctx) throw new Error('useSales must be used within SalesProvider');
  return ctx;
};
